import { ref, watch, type Ref } from 'vue'
import type { ColumnLayoutState, ColumnProps } from '../interface'

interface ColumnSettingsStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

interface UseColumnSettingsOptions {
  storageKey?: string | (() => string | undefined)
  storage?: ColumnSettingsStorage
}

const STORAGE_PREFIX = 'ask-spinner-admin:table-columns:'

function cloneColumns<T extends ColumnProps>(columns: T[]) {
  return columns.map(item => ({
    colEnable: true,
    colShow: true,
    searchOrder: 0,
    ...item
  })) as T[]
}

function isLayoutState(value: unknown): value is ColumnLayoutState {
  if (!value || typeof value !== 'object') return false
  const state = value as Partial<ColumnLayoutState>
  return state.version === 1 && Array.isArray(state.order) && !!state.visibility && !!state.fixed
}

export function useColumnSettings<T extends ColumnProps = ColumnProps>(options: UseColumnSettingsOptions = {}) {
  const columns = ref<T[]>([]) as Ref<T[]>
  let sourceColumns: T[] = []
  let memoryState: ColumnLayoutState | undefined

  function resolvedStorageKey() {
    const key = typeof options.storageKey === 'function' ? options.storageKey() : options.storageKey
    return key ? `${STORAGE_PREFIX}${key}` : undefined
  }

  function resolvedStorage() {
    if (options.storage) return options.storage
    if (typeof window === 'undefined') return undefined
    return window.localStorage
  }

  function captureLayout(items: T[]): ColumnLayoutState {
    return {
      version: 1,
      order: items.map(item => item.prop),
      visibility: Object.fromEntries(items.map(item => [item.prop, item.colShow !== false])),
      fixed: Object.fromEntries(items.map(item => [item.prop, item.fixed ?? null]))
    }
  }

  function loadPersistedLayout() {
    const key = resolvedStorageKey()
    const storage = resolvedStorage()
    if (!key || !storage) return undefined
    try {
      const raw = storage.getItem(key)
      if (!raw) return undefined
      const parsed: unknown = JSON.parse(raw)
      return isLayoutState(parsed) ? parsed : undefined
    } catch {
      return undefined
    }
  }

  function persistLayout(state: ColumnLayoutState) {
    const key = resolvedStorageKey()
    const storage = resolvedStorage()
    if (!key || !storage) return
    try {
      storage.setItem(key, JSON.stringify(state))
    } catch {
      // 浏览器禁用存储或容量不足时，保留当前会话内的列设置即可。
    }
  }

  function applyLayout(source: T[], state?: ColumnLayoutState) {
    if (!state) return cloneColumns(source)
    const sourceMap = new Map(cloneColumns(source).map(item => [item.prop, item]))
    const ordered: T[] = []
    for (const prop of state.order) {
      const item = sourceMap.get(prop)
      if (!item) continue
      ordered.push(item)
      sourceMap.delete(prop)
    }
    ordered.push(...sourceMap.values())
    return ordered.map(item => ({
      ...item,
      colShow: state.visibility[item.prop] ?? item.colShow,
      fixed: state.fixed[item.prop] ?? item.fixed
    })) as T[]
  }

  function setColumns(source: T[]) {
    sourceColumns = cloneColumns(source)
    const layout = loadPersistedLayout() ?? memoryState
    columns.value = applyLayout(sourceColumns, layout)
    memoryState = captureLayout(columns.value)
  }

  function resetColumnSettings() {
    const key = resolvedStorageKey()
    const storage = resolvedStorage()
    if (key && storage) {
      try {
        storage.removeItem(key)
      } catch {
        // 存储不可用时仍可恢复当前会话中的默认布局。
      }
    }
    memoryState = undefined
    columns.value = cloneColumns(sourceColumns)
    memoryState = captureLayout(columns.value)
  }

  watch(
    columns,
    value => {
      if (!value.length) return
      memoryState = captureLayout(value)
      persistLayout(memoryState)
    },
    { deep: true, flush: 'post' }
  )

  return {
    columns,
    setColumns,
    resetColumnSettings,
    getColumnLayout: () => captureLayout(columns.value)
  }
}
